import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpParams } from '@angular/common/http';
import { Facet } from '@stylehaven/components';
import { PagedResult } from '../../entities/common/paged-result.entity';
import { Member } from '../../entities/user/member.entity';
import { MapObjectToHttpParams } from '../../helpers/map-obj-to-http-params.helper';
import { ApiList } from '../../record/entities/api-list.entity';
import { Metadata } from '../../record/entities/metadata.entity';
import { PaginatedResults } from '../../record/entities/pagination/paginated-results.entity';
import { ApiListToPaginatedResults } from '../../record/helpers/api-list-to-paginated-results.helper';
import { AbstractHttpService } from '../../services/http/http-service.abstract';
import { TaskSearch } from '../entities/task-search.entity';
import { BaseTask, Task, TaskAction, TaskVariables } from '../entities/task.entity';

@Injectable()
export class TaskService {
    private readonly ENDPOINT: string = 'tasks';
    private readonly BASE_QUERY: string = '+DeleteStatus:NotDeleted';

    constructor(private readonly http: AbstractHttpService) {}

    // v1 api call used by Edepot and Seriesregister
    getAllTasks(taskSearch?: TaskSearch): Observable<PagedResult<Task>> {
        return this.http.get<PagedResult<Task<any>>>(this.ENDPOINT, this.taskSearchToHttpParams(taskSearch));
    }

    // v2 api call used by DepotManagement, ArchiveManagement, Edepot and Seriesregister
    getAll<K = string, V = TaskVariables>(taskSearch: TaskSearch): Observable<PaginatedResults<Task<K, V>>> {
        return this.http
            .get<ApiList<BaseTask<Metadata, K, V>>>(this.ENDPOINT, this.taskSearchToHttpParams(taskSearch), 'v2')
            .pipe(
                map(apiList =>
                    ApiListToPaginatedResults<BaseTask<Metadata, K, V>, Task<K, V>>(
                        apiList,
                        taskSearch.NrOfResults,
                        baseTask => new Task(baseTask)
                    )
                )
            );
    }

    getById(id: string): Observable<Task> {
        return this.http.get<BaseTask<Metadata>>(`${this.ENDPOINT}/${id}`).pipe(map(baseTask => new Task(baseTask)));
    }

    getEvaluators(): Observable<Member[]> {
        return this.http.get<Member[]>(`${this.ENDPOINT}/evaluators`);
    }

    getFacets(taskSearch?: TaskSearch): Observable<Facet[]> {
        return this.http.get<Facet[]>(`${this.ENDPOINT}/facets`, this.taskSearchToHttpParams(taskSearch));
    }

    complete(id: string, action: TaskAction): Observable<void> {
        return this.http.post(`${this.ENDPOINT}/${id}/action`, action);
    }

    // Utils
    private taskSearchToHttpParams(taskSearch: TaskSearch | undefined): HttpParams {
        taskSearch = taskSearch ?? {};
        const enrichedQuery = taskSearch?.Q ? `${taskSearch.Q} ${this.BASE_QUERY}` : this.BASE_QUERY;

        return MapObjectToHttpParams({ ...taskSearch, Q: enrichedQuery }, true);
    }
}
